import * as React from 'react'

export interface OctopusIconProps {
  /** Tentacle wave amplitude (degrees) */
  tentacleWave?: number
  /** Tentacle wave speed (per second) */
  tentacleSpeed?: number
  /** Blink phase offset (seconds) */
  blinkPhase?: number
  /** Blink interval (seconds) */
  blinkInterval?: number
  /** Blink duration (seconds) */
  blinkDuration?: number
  /** Transparent background */
  transparent?: boolean
  /** Icon size (px) */
  size?: number | string
  /** Extra class name */
  className?: string
}

function useRafTime(): number {
  const [time, setTime] = React.useState(0)
  const startRef = React.useRef<number | null>(null)
  const rafRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    const tick = (now: number) => {
      if (startRef.current === null) {
        startRef.current = now
      }
      setTime((now - startRef.current) / 1000)
      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  return time
}

/**
 * Pixel octopus icon with runtime animation.
 * Tentacles are grouped into left/middle/right with phase offsets.
 */
export function OctopusIcon({
  tentacleWave = 4,
  tentacleSpeed = 4,
  blinkPhase = 0,
  blinkInterval = 2,
  blinkDuration = 0.15,
  transparent = true,
  size = 240,
  className,
}: OctopusIconProps) {
  const t = useRafTime()

  // Tentacle wave
  const waveL = Math.sin(t * tentacleSpeed + 0) * tentacleWave
  const waveM = Math.sin(t * tentacleSpeed + 2) * tentacleWave
  const waveR = Math.sin(t * tentacleSpeed + 4) * tentacleWave

  // Blink
  const blinkCycle = (t + blinkPhase) % blinkInterval
  const isBlinking = blinkCycle < blinkDuration
  const eyeScaleY = isBlinking ? 0.1 : 1

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ display: 'block', imageRendering: 'pixelated' }}
      shapeRendering="crispEdges"
    >
      <defs>
        <linearGradient id="octoBgGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FAFAFA" />
          <stop offset="100%" stopColor="#EBEBEB" />
        </linearGradient>
        <linearGradient id="octoGloss" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="white" stopOpacity="0.25" />
          <stop offset="12%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>

      <g transform="translate(12,12) scale(0.96) translate(-12,-12)">
        {!transparent && (
          <>
            <rect x="2" y="2" width="20" height="20" rx="4.5" ry="4.5" fill="url(#octoBgGrad)" />
            <rect x="2" y="2" width="20" height="20" rx="4.5" ry="4.5" fill="url(#octoGloss)" />
          </>
        )}

        <g fill="#9570BE">
          <rect x="8" y="4" width="8" height="2" />
          <rect x="6" y="5" width="12" height="2" />
          <rect x="5.5" y="7" width="13" height="1.5" />

          <g transform={`translate(8.25, 6.25) scale(1, ${eyeScaleY}) translate(-8.25, -6.25)`}>
            <rect x="7.5" y="5.5" width="1.5" height="1.5" fill="white" />
            <rect x="8" y="6" width="0.8" height="0.8" fill="#333" />
          </g>
          <g transform={`translate(15.75, 6.25) scale(1, ${eyeScaleY}) translate(-15.75, -6.25)`}>
            <rect x="15" y="5.5" width="1.5" height="1.5" fill="white" />
            <rect x="15.5" y="6" width="0.8" height="0.8" fill="#333" />
          </g>

          <rect x="10" y="7.5" width="4" height="4.5" />

          <g transform={`rotate(${waveL}, 6, 14)`}>
            <rect x="7.5" y="12" width="2.5" height="2" />
            <rect x="5.5" y="14" width="2.5" height="2" />
            <rect x="4" y="16" width="2" height="2" />
          </g>

          <g transform={`rotate(${waveM}, 12, 14)`}>
            <rect x="10" y="12" width="4" height="2" />
            <rect x="9.5" y="14" width="2.5" height="2" />
            <rect x="12" y="14" width="2.5" height="2" />
            <rect x="9" y="16" width="2" height="2" />
            <rect x="13" y="16" width="2" height="2" />
          </g>

          <g transform={`rotate(${waveR}, 18, 14)`}>
            <rect x="14" y="12" width="2.5" height="2" />
            <rect x="16" y="14" width="2.5" height="2" />
            <rect x="18" y="16" width="2" height="2" />
          </g>
        </g>
      </g>
    </svg>
  )
}
